import { useState } from "react";
import { FilterBase } from "@components/commerce/product-listing/filters/common/filter/FilterBase";
import { Text } from "@ui/components/content/text/Text";

import {
    styles,
    localCheckboxStyle,
} from "@components/commerce/product-listing/filters/toolbar/item/common/CommonToolbarFilterStyles";
import { HIERARCHICAL_CATEGORIES_SEPARATOR } from "@lib/constants/filtersConstants";
import type { RefinementListItem } from "instantsearch.js/es/connectors/refinement-list/connectRefinementList";
import { useRouter } from "next/router";
import { useMoney } from "ui/src/components/data-display/money/useMoney";
import { activeSiteCurrency } from "@config/site/site-config";
import { Box } from "@ui/components/layout/box/Box";
import { scrollbarStyles } from "@ui/css/01.scrollbar/scrollbar.styles";
import { SelectDropdownItem } from "@ui/components/overlay/select-dropdown/SelectDropdown";
import { Checkbox } from "@ui/components/forms/checkbox/Checkbox";
import { focusOutline } from "@ui/css/02.focus/focus.styles";

export interface PriceToolbarFilterProps {
    initialFilters: RefinementListItem[];
    filterName: string;
    items: RefinementListItem[];
    refine: (value: string) => void;
    isToolbar: boolean;
    isLoading: boolean;
}

export const PriceToolbarFilter = ({
    initialFilters,
    items,
    refine,
    filterName,
    isToolbar,
    isLoading,
}: PriceToolbarFilterProps) => {
    const dropdownItems = (
        <Box
            sx={{
                ...scrollbarStyles,
                height: "auto",
                maxHeight: "500px",
                overflowY: "auto",
                mx: 2,
                padding: 2,
                bgColor: "white",
            }}
        >
            {initialFilters?.map((item) => {
                const categoryHierarchy = item.label.split(HIERARCHICAL_CATEGORIES_SEPARATOR);
                const dynamicItem = items.find((it) => it.value === item.value);
                return (
                    <SelectDropdownItem
                        key={item.label}
                        sx={localCheckboxStyle}
                        onClick={() => {
                            refine(item.value);
                        }}
                        isDisabled={!dynamicItem}
                    >
                        <CheckboxWithHoveredState
                            dynamicItem={dynamicItem}
                            item={item}
                            categoryHierarchy={categoryHierarchy}
                            categoryRefine={refine}
                        />
                    </SelectDropdownItem>
                );
            })}
        </Box>
    );

    return (
        <FilterBase
            isLoading={isLoading}
            dropdownItems={dropdownItems}
            filterName={filterName}
            isToolbar={isToolbar}
        />
    );
};

const CheckboxWithHoveredState = ({ categoryHierarchy, dynamicItem, item, categoryRefine }) => {
    const [hovered, setHovered] = useState(false);
    const { locale } = useRouter();
    const money = useMoney({
        money: {
            centAmount: 0,
            currencyCode: activeSiteCurrency,
            fractionDigits: 0,
        },
        locale,
        convertFromCents: true,
    });

    return (
        <Box
            sx={styles.itemWrapper}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <Text noOfLines={1}>
                {money.currencyNarrowSymbol + " " + categoryHierarchy[categoryHierarchy.length - 1]}
            </Text>
            {dynamicItem && (
                <>
                    <Text color={"gray.900"}>{dynamicItem?.count}</Text>
                    <Checkbox
                        _focus={{
                            ...focusOutline,
                        }}
                        colorScheme={dynamicItem?.isRefined || !hovered ? "black" : "gray"}
                        key={item.label}
                        value={item.label}
                        isChecked={hovered || dynamicItem?.isRefined}
                        isDisabled={!dynamicItem}
                        onChange={() => {
                            categoryRefine(item.value);
                        }}
                    />
                </>
            )}
        </Box>
    );
};

export default PriceToolbarFilter;
